import { sha256 } from 'js-sha256'

/**
 * `crypto.subtle` yalnız **secure context**te (HTTPS veya `localhost`/`127.0.0.1`) tanımlıdır.
 * Uygulama `http://<ip>:port` gibi güvensiz bir origin'den açıldığında `crypto.subtle` **yoktur**;
 * oidc-client-ts'in PKCE `code_challenge` üretimi (`crypto.subtle.digest('SHA-256', …)`) bu yüzden
 * "Crypto.subtle is available only in secure contexts (HTTPS)." hatasıyla patlar ve **giriş hiç
 * başlamaz** (Firefox'ta güvensiz origin'den açılınca yaşanan çıkmazın kök nedeni budur).
 *
 * Bu shim, güvensiz bağlamda eksik olan **tek** parçayı — SHA-256 `digest` — saf-JS (`js-sha256`)
 * ile karşılar. PKCE S256 challenge'ı SHA-256(verifier)'ın base64url'ü olduğundan çıktı native
 * `crypto.subtle` ile **birebir aynıdır** (Keycloak doğrulaması bozulmaz). Bu bir güvenlik
 * zayıflatması değil, uyumluluk fallback'idir. Secure context'te (native `crypto.subtle` varken)
 * **hiçbir şey yapılmaz** — daima native kullanılır.
 *
 * Not: yalnız `digest` sağlanır. `getRandomValues` (PKCE verifier üretimi) güvensiz bağlamda zaten
 * çalışır; DPoP'a özgü `subtle.sign`/`generateKey`/`randomUUID` bu akışta (Auth Code + PKCE, DPoP
 * kapalı) çağrılmaz. **Kalıcı/doğru çözüm** dağıtımı HTTPS'e (veya `localhost`'a) taşımaktır; shim
 * yalnız HTTPS elde olmayan iç-ağ IP dağıtımını mümkün kılar. dva-poc aynı sorunu `@openid/appauth`
 * `deriveChallenge` override'ıyla çözmüştü; burada kütüphane oidc-client-ts olduğundan global
 * `crypto.subtle.digest`'i besliyoruz.
 *
 * **Import sırası kritik:** `main.tsx`'te AuthProvider/userManager'dan **önce** (side-effect) import
 * edilir; shim, ilk `signinRedirect` çağrılmadan önce kurulmuş olmalıdır.
 */
function normalizeAlg(algorithm: AlgorithmIdentifier): string {
  return (typeof algorithm === 'string' ? algorithm : algorithm.name).toUpperCase()
}

function installCryptoSubtlePolyfill(): void {
  if (typeof globalThis.crypto === 'undefined') return
  // Secure context → native subtle var; dokunma.
  if (globalThis.crypto.subtle) return

  const subtle: Pick<SubtleCrypto, 'digest'> = {
    digest(algorithm: AlgorithmIdentifier, data: BufferSource): Promise<ArrayBuffer> {
      const alg = normalizeAlg(algorithm)
      if (alg !== 'SHA-256') {
        return Promise.reject(
          new Error(`crypto.subtle polyfill yalnız SHA-256 destekler (istenen: ${alg}).`),
        )
      }
      // js-sha256 Uint8Array/ArrayBuffer kabul eder; oidc-client-ts buraya TextEncoder çıktısı
      // (Uint8Array) geçirir. Çıktı native digest ile aynı ham SHA-256 bayt dizisidir.
      return Promise.resolve(sha256.arrayBuffer(data as ArrayBuffer))
    },
  }

  try {
    Object.defineProperty(globalThis.crypto, 'subtle', {
      value: subtle,
      configurable: true,
    })
  } catch {
    // `subtle` yeniden tanımlanamıyorsa (nadiren non-configurable) sessiz geç: RequireAuth guard'ı
    // `crypto.subtle` yokluğunu yakalayıp çıkmaz yerine "HTTPS gerekli" uyarısı gösterir.
  }
}

installCryptoSubtlePolyfill()
